import { Globe, Layers } from 'lucide-react';
import type { ScopeState } from '../../lib/scope';
import { Select } from '../ui/input';
import { cn } from '../../lib/utils';
import { useProjectTree } from '../../lib/project-tree';

/**
 * The filter that replaced the project in the URL.
 *
 * Two controls, because scope is two questions: *which* project, and *how deep*. "All projects" is
 * the first option and the default — these pages read across the whole organization, and narrowing is
 * something you do afterwards. The depth toggle appears only once a project is chosen, since the
 * organization plus everything under it already is everything, and offering "organization only" beside
 * it would just be a way to accidentally empty the page.
 */
export function ScopeBar({
  scope,
  /** Rendered on the right — result counts, per-page filters, actions. */
  children,
  className,
}: {
  scope: ScopeState;
  children?: React.ReactNode;
  className?: string;
}) {
  const { allProjects } = useProjectTree();

  return (
    <div className={cn('flex items-center gap-2 flex-wrap', className)}>
      <div className="relative flex items-center">
        <Globe size={13} className="absolute left-2.5 text-ink-faint pointer-events-none" />
        <Select
          value={scope.projectId ?? ''}
          onChange={(e) => scope.setProjectId(e.target.value || null)}
          className="pl-7 py-1.5 text-xs max-w-[260px]"
          title="Which project this page is reading"
        >
          <option value="">All projects</option>
          {allProjects.map((project) => (
            <option key={project.id} value={project.id}>
              {/* Non-breaking spaces: a <select> collapses ordinary leading whitespace, and without
                  the indent a deep tree reads as a flat list of unrelated names. */}
              {'  '.repeat(Math.max(project.depth - 1, 0))}
              {project.depth > 1 ? '└ ' : ''}
              {project.name}
            </option>
          ))}
        </Select>
      </div>

      {scope.projectId && (
        <button
          onClick={() => scope.setMode(scope.mode === 'cone' ? 'self' : 'cone')}
          title={
            scope.mode === 'cone'
              ? 'Including every sub-project beneath this one'
              : 'This project alone, ignoring its sub-projects'
          }
          className={cn(
            'inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium border transition-colors',
            scope.mode === 'cone'
              ? 'bg-card text-ink border-border-strong'
              : 'bg-card text-ink-muted border-border hover:border-orange-300 hover:text-ink',
          )}
        >
          <Layers size={11} />
          {scope.mode === 'cone' ? 'With sub-projects' : 'This project only'}
        </button>
      )}

      {children && <div className="ml-auto flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

/**
 * Which project a result came from.
 *
 * Every one of these pages can now show rows from several projects at once, and paths are
 * project-relative — so without this, two pages called `auth/sessions` from different projects are
 * indistinguishable. Suppressed when the page is already narrowed to one project, where it would be
 * the same word on every row.
 */
export function ProjectTag({
  name,
  className,
}: {
  name: string | null | undefined;
  className?: string;
}) {
  if (!name) return null;
  return (
    <span
      className={cn(
        'flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-surface-muted text-ink-muted',
        className,
      )}
    >
      {name}
    </span>
  );
}
